// A checklist run, read out loud: what it found, why it matters, what to do,
// and whether an agent can do it or a person has to.
//
// One implementation, two readers. The run card shows it as "What this means",
// and "Send to agent queue" turns the agent-fixable part into an ordinary
// fulfillment job (task + ai_job), the same shape Retroactive Optimization uses.
// The job carries brief.hosting_fix.run_id, so a run can find the job it spawned
// without a column of its own.

import { supabase } from './supabase';
import { enqueueAiJob, HOSTING_FIX_MODEL, type AiJob } from './aiJobs';
import { fetchRunStepsFor, type HostedSite, type HostingAccount, type HostingRun, type HostingRunStep } from './hosting';
import type { JobBrief } from './supabase.types';
import { PROMPT_DEFAULTS, loadPromptTemplate, renderPrompt, type PromptId } from './prompts';

export type FixUrgency = 'urgent' | 'soon' | 'later';
export type FixOwner = 'agent' | 'person';

export type FixItem = {
  key: string;
  site_id: string | null;
  site: string | null;
  step: string;
  urgency: FixUrgency;
  owner: FixOwner;
  /** One line, plain English: the problem. */
  title: string;
  /** Why it matters, for someone who doesn't live in package.json. */
  why: string;
  /** What fixing it means, concretely. */
  action: string;
};

type Detail = Record<string, unknown>;
const arr = (v: unknown): Detail[] => (Array.isArray(v) ? (v as Detail[]) : []);
const str = (v: unknown): string | null => (typeof v === 'string' && v ? v : null);
const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const host = (url: string) => url.replace(/^https?:\/\//i, '').replace(/\/.*$/, '');
const plural = (n: number, word: string) => (n === 1 ? `${n} ${word}` : `${n} ${/[^aeiou]y$/.test(word) ? `${word.slice(0, -1)}ies` : `${word}s`}`);

const URGENCY_ORDER: Record<FixUrgency, number> = { urgent: 0, soon: 1, later: 2 };

/** Every problem a run's steps reported, translated. Steps that passed or were skipped say nothing. */
export function buildFixPlan(steps: HostingRunStep[], sites: HostedSite[]): FixItem[] {
  const items: FixItem[] = [];
  // Packages an audit fix already upgrades, so "major versions behind" doesn't ask for them twice.
  const upgradedByAudit = new Set<string>();
  const add = (s: HostingRunStep, item: Omit<FixItem, 'key' | 'site_id' | 'site' | 'step'>, suffix = '') => {
    const site = sites.find((x) => x.id === s.site_id) || null;
    items.push({ key: `${s.id}${suffix}`, site_id: s.site_id, site: site ? host(site.url) : null, step: s.step, ...item });
  };

  // Render and Lighthouse findings need a way in: a repo (Netlify) or SSH + WP-CLI (Cloudways).
  const reachable = (siteId: string | null) => {
    const site = sites.find((x) => x.id === siteId);
    return Boolean(site?.netlify_site_id || site?.cloudways_app_id);
  };

  for (const s of steps) {
    if (s.status !== 'attention' && s.status !== 'failed') continue;
    // A site moved off the account since this run: its findings belong to its new account, not this one.
    if (s.site_id && !sites.some((x) => x.id === s.site_id)) continue;
    const d = (s.detail || {}) as Detail;

    if (s.status === 'failed') {
      add(s, {
        urgency: 'soon',
        owner: 'person',
        title: `${s.title} couldn't run`,
        why: 'The check itself broke, so this part of the site was not checked at all.',
        action: `Look at the error and run the checklist again: ${s.summary || 'no message'}`,
      });
      continue;
    }

    switch (s.step) {
      case 'payment':
        add(s, {
          urgency: 'urgent',
          owner: 'person',
          title: s.summary || 'Payment needs a look',
          why: 'Hosting is only managed while it is paid for.',
          action: 'Check Stripe. If they are behind, send the 7-day notice before the site goes down.',
        });
        break;
      case 'uptime':
        add(s, {
          urgency: 'soon',
          owner: 'person',
          title: s.summary || 'Uptime below target',
          why: 'Visitors hit an error page while the site was down.',
          action: 'Open the uptime chart, see when it happened, and tell Kyle if it was long or repeated.',
        });
        break;

      // ─── Netlify ───
      case 'deploy': {
        const failed = num(d.failed_since_published) || 0;
        if (failed) {
          add(s, {
            urgency: 'urgent',
            owner: 'agent',
            title: `${plural(failed, 'production deploy')} failed since the live one`,
            why: 'Every change since then is stuck: the site is still serving the last deploy that worked.',
            action: 'Find out why the deploy fails (Netlify deploy log) and fix the build.',
          }, ':failed');
        }
        if (d.locked || d.stop_builds) {
          add(s, {
            urgency: 'soon',
            owner: 'person',
            title: d.locked ? 'Auto-publishing is locked to an old deploy' : 'Builds are stopped for this site',
            why: 'New commits won’t go live until someone unlocks it.',
            action: 'In Netlify, unlock auto-publishing (or re-enable builds) if nobody locked it on purpose.',
          }, ':locked');
        }
        break;
      }
      case 'headers': {
        const missing = arr(d.missing).map(String).filter(Boolean);
        if (d.serves_netlify === false) {
          add(s, {
            urgency: 'urgent',
            owner: 'person',
            title: 'The domain is not answering from Netlify',
            why: 'Either DNS moved somewhere else or a proxy sits in front. Our checks may be looking at the wrong site.',
            action: 'Check the domain’s DNS and confirm with the client where the site should live.',
          }, ':dns');
        }
        const cert = (d.cert || {}) as Detail;
        const days = num(cert.days_left);
        if (days !== null && days < 21) {
          add(s, {
            urgency: days < 7 ? 'urgent' : 'soon',
            owner: 'person',
            title: `The HTTPS certificate expires in ${plural(days, 'day')}`,
            why: 'When it expires, browsers show a full-page security warning.',
            action: 'Netlify renews it on its own when DNS points at Netlify; if it hasn’t, check the domain settings in Netlify.',
          }, ':cert');
        }
        if (d.https_redirect === false) {
          add(s, {
            urgency: 'soon',
            owner: 'person',
            title: 'http:// does not redirect to https://',
            why: 'Visitors who type the address can land on the unencrypted version.',
            action: 'Turn on "Force HTTPS" in the site’s Netlify domain settings.',
          }, ':https');
        }
        if (missing.length) {
          add(s, {
            urgency: 'soon',
            owner: 'agent',
            title: `Missing security headers: ${missing.join(', ')}`,
            why: 'They stop other sites from framing this one (clickjacking) and stop browsers from guessing file types. Standard on every site we ship.',
            action: 'Add a [[headers]] block for "/*" to netlify.toml with the missing headers.',
          }, ':headers');
        }
        break;
      }
      case 'secrets': {
        const findings = arr(d.findings);
        const kinds = [...new Set(findings.map((f) => str(f.kind)).filter(Boolean))] as string[];
        add(s, {
          urgency: 'urgent',
          owner: 'person',
          title: `A secret is exposed in the public site: ${kinds.join(', ') || 'see the step'}`,
          why: 'Anyone can download it from the browser and use it with full access.',
          action: 'Rotate (replace) the key now in the service it belongs to. Then an agent moves its use server-side so it never ships to the browser again.',
        }, ':rotate');
        add(s, {
          urgency: 'urgent',
          owner: 'agent',
          title: `Move ${kinds.join(', ') || 'the exposed key'} out of the browser bundle`,
          why: 'Rotating alone doesn’t help if the new key ships the same way.',
          action: 'Find where the key is read in client code and move that call behind a Netlify function or server endpoint; the browser only ever gets public keys.',
        }, ':move');
        break;
      }
      case 'build':
        add(s, {
          urgency: 'urgent',
          owner: 'agent',
          title: 'The next deploy would fail',
          why: 'The site still serves the last good deploy, but nothing new (content, fixes) can go live until the build works.',
          action: `Make a clean install + build pass again with Node ${str(d.node) || 'the pinned version'}. ${s.summary || ''}`.trim(),
        });
        break;
      case 'audit': {
        if (d.source === 'dependabot') {
          // Dependabot owns dependency security (managed-hosting skill): set it up, review its PRs,
          // and only chase a fix by hand when one exists and no PR carries it.
          const repo = str(d.repo) || 'the repo';
          const setup = (d.setup || {}) as Detail;
          if (setup.alerts !== true || setup.security_updates !== true || setup.config !== true) {
            add(s, {
              urgency: 'soon',
              owner: 'agent',
              title: `Dependabot isn’t fully set up for ${repo}`,
              why: 'Dependabot is GitHub’s free update bot. Once it is on, every security fix arrives as a ready-made PR the day it is released, and it never suggests a downgrade.',
              action: 'Turn on Dependabot alerts and security update PRs for the repo, and add the .github/dependabot.yml from the managed-hosting skill. Config only; the site does not change.',
            }, ':setup');
          }
          const prs = arr(d.prs);
          if (prs.length) {
            add(s, {
              urgency: 'soon',
              owner: 'agent',
              title: `${plural(prs.length, 'Dependabot update')} waiting for review: ${prs.map((p) => `#${num(p.number) ?? '?'}`).join(', ')}`,
              why: 'Each PR is an update Dependabot prepared. Someone has to confirm the site still looks and works the same before it goes live.',
              action: `Review ${prs.map((p) => `#${num(p.number) ?? '?'} (${str(p.title) || 'update'})`).join('; ')}: let each deploy preview build, compare it with production page by page, and push fixes to the Dependabot branch if anything breaks. Report which are safe to merge; a person merges.`,
            }, ':prs');
          }
          const unclaimed = arr(d.alerts).filter(
            (a) => (a.severity === 'critical' || a.severity === 'high') && str(a.fix) && a.scope !== 'development' && a.has_pr !== true && (a.severity === 'critical' || a.relationship === 'direct'),
          );
          if (unclaimed.length) {
            const critical = unclaimed.some((a) => a.severity === 'critical');
            add(s, {
              urgency: critical ? 'urgent' : 'soon',
              owner: 'agent',
              title: `${plural(unclaimed.length, 'security fix')} available with no PR yet: ${[...new Set(unclaimed.map((a) => str(a.package)))].join(', ')}`,
              why: 'A patched version exists for a package the live site uses, but Dependabot couldn’t prepare the update on its own, usually because it needs a major upgrade.',
              action: `Upgrade ${unclaimed.map((a) => `${str(a.package)} to ${str(a.fix)} or later`).join('; ')}, following the official upgrade guide for any major version jump.`,
            }, ':fix');
          }
          break;
        }
        const counts = (d.counts || {}) as Detail;
        const critical = num(counts.critical) || 0;
        const high = num(counts.high) || 0;
        // Group advisories by the fix that clears them, so "upgrade X" reads as one task.
        const fixes = new Map<string, { names: string[]; worst: string }>();
        for (const a of arr(d.advisories)) {
          const fix = str(a.fix) || 'no fix published yet';
          const m = fix.match(/^(@?[^@\s]+)@/);
          if (m) upgradedByAudit.add(m[1]);
          const entry = fixes.get(fix) || { names: [], worst: 'low' };
          entry.names.push(String(a.name));
          if (a.severity === 'critical' || (a.severity === 'high' && entry.worst !== 'critical')) entry.worst = String(a.severity);
          fixes.set(fix, entry);
        }
        const steps = [...fixes.entries()]
          .sort((a, b) => (a[1].worst === 'critical' ? -1 : 0) - (b[1].worst === 'critical' ? -1 : 0))
          .map(([fix, e]) => (fix === 'npm audit fix' ? `run npm audit fix (clears ${plural(e.names.length, 'package')})` : fix === 'no fix published yet' ? `no fix yet for ${e.names.join(', ')}` : `upgrade to ${fix} (clears ${e.names.join(', ')})`));
        add(s, {
          urgency: critical ? 'urgent' : 'soon',
          owner: 'agent',
          title: `${plural(critical + high, 'known vulnerability')} in the site’s dependencies (${critical} critical, ${high} high)`,
          why: 'These are published security holes in packages the site is built from. Some only matter at build time, but nobody should have to judge that case by case: clear the critical ones first.',
          action: `Update dependencies: ${steps.slice(0, 5).join('; ')}${steps.length > 5 ? '; …' : ''}.`,
        });
        break;
      }
      case 'versions': {
        const node = (d.node || {}) as Detail;
        if (node.status === 'unpinned') {
          add(s, {
            urgency: 'soon',
            owner: 'agent',
            title: 'Node version is not pinned',
            why: 'Netlify keeps building with whatever version the site started on; when that changes underneath us, builds break without any code change (this is what broke West Metro this morning).',
            action: 'Add an .nvmrc with the Node version the site builds with (22), matching what the checklist tested.',
          }, ':pin');
        } else if (node.status === 'eol') {
          add(s, {
            urgency: 'urgent',
            owner: 'agent',
            title: `Node ${str(node.version) || ''} is past end of life`,
            why: 'It no longer gets security fixes.',
            action: 'Move the pin to Node 22 or 24 and confirm the build still passes.',
          }, ':eol');
        }
        const behind = arr(d.packages).filter((p) => (num(p.majors_behind) || 0) >= 1 && !upgradedByAudit.has(String(p.name)));
        if (behind.length) {
          add(s, {
            urgency: 'later',
            owner: 'agent',
            title: `Major versions behind: ${behind.map((p) => `${p.name} ${p.installed} → ${p.latest}`).join(', ')}`,
            why: 'Old majors stop getting fixes, and each skipped major makes the eventual upgrade bigger.',
            action: 'Upgrade following each package’s official upgrade guide, one major at a time, keeping the site looking the same.',
          }, ':majors');
        }
        break;
      }
      case 'render':
        add(s, {
          urgency: 'soon',
          owner: reachable(s.site_id) ? 'agent' : 'person',
          title: 'Pages render differently or not at all',
          why: 'Something visitors see changed or broke.',
          action: `Open the screenshots in the step and check: ${s.summary || ''}`.trim(),
        });
        break;
      case 'lighthouse':
        add(s, {
          urgency: 'later',
          owner: reachable(s.site_id) ? 'agent' : 'person',
          title: `Lighthouse below 50 on mobile: ${s.summary?.replace(/^Below 50: /, '').split('. (')[0] || ''}`,
          why: 'Slow mobile pages lose visitors and rank lower.',
          action: 'Work through the biggest Lighthouse opportunities (images, render-blocking scripts, unused JS).',
        });
        break;

      // ─── Cloudways / WordPress: an agent works them over SSH + WP-CLI, after a snapshot ───
      case 'updates':
        add(s, {
          urgency: 'soon',
          owner: 'agent',
          title: s.summary || 'WordPress updates need a look',
          why: 'Out-of-date plugins are the most common way WordPress sites get hacked.',
          action: 'Snapshot, then apply the remaining updates with WP-CLI and find out why any failed (a premium plugin may need its licence or the theme’s plugin installer). Check the pages look the same afterwards.',
        });
        break;
      case 'smtp':
        add(s, {
          urgency: 'urgent',
          owner: 'agent',
          title: s.summary || 'Site email isn’t arriving',
          why: 'Contact-form messages from customers may be going nowhere.',
          action: 'Find out why mail isn’t going through WP Mail SMTP (plugin off, mailer disconnected, expired key) and fix it. If it needs a login or key only the client or Kyle has, say exactly which.',
        });
        break;
      case 'security':
        add(s, {
          urgency: 'soon',
          owner: 'agent',
          title: s.summary || 'Wordfence needs a look',
          why: 'Open findings are either real problems or noise that hides the next real one.',
          action: 'Work each Wordfence finding: update a vulnerable plugin, propose a replacement for an abandoned one, turn Wordfence on if it is off. A finding that is expected gets a one-line reason to accept it.',
        });
        break;
      default:
        add(s, { urgency: 'soon', owner: 'person', title: s.summary || s.title, why: '', action: 'Open the step for details.' });
    }
  }
  return items.sort((a, b) => URGENCY_ORDER[a.urgency] - URGENCY_ORDER[b.urgency] || (a.owner === b.owner ? 0 : a.owner === 'agent' ? -1 : 1));
}

/** Repo + Netlify site for a site, from that run's deploy step (the runner already looked them up). */
export function repoOf(steps: HostingRunStep[], siteId: string | null): { repo: string; branch: string; netlify: string | null } | null {
  const d = (steps.find((s) => s.site_id === siteId && s.step === 'deploy')?.detail || {}) as Detail;
  const repo = str(d.repo);
  return repo ? { repo, branch: str(d.branch) || 'main', netlify: str(d.site_name) } : null;
}

type RepoTarget = { repo: string; branch: string; netlify: string | null };
type CloudwaysTarget = { server_id: string; app_id: string };
export type FixTarget = ({ platform: 'netlify' } & RepoTarget) | ({ platform: 'cloudways' } & CloudwaysTarget);

/** Where an agent works a site's fixes: a Cloudways app over SSH, or a repo. Same precedence as sitePlatform. */
export function fixTargetOf(steps: HostingRunStep[], site: HostedSite): FixTarget | null {
  if (site.cloudways_app_id && site.cloudways_server_id) return { platform: 'cloudways', server_id: site.cloudways_server_id, app_id: site.cloudways_app_id };
  const repo = repoOf(steps, site.id);
  return repo ? { platform: 'netlify', ...repo } : null;
}

export type HostingFixBrief = JobBrief & {
  hosting_fix: {
    run_id: string;
    account_id: string;
    site_id: string | null;
    site_url: string;
    platform: 'netlify' | 'cloudways';
    /** Netlify sites. */
    repo?: string;
    branch?: string;
    /** Cloudways sites. */
    cloudways_server_id?: string;
    cloudways_app_id?: string;
    items: Array<Pick<FixItem, 'urgency' | 'title' | 'action' | 'step'>>;
  };
};

/** Which prompt a site's fixes render from: WordPress on Cloudways and Netlify repos get different rules. */
export const fixPromptId = (target: FixTarget): PromptId => (target.platform === 'cloudways' ? 'hosting-fix-wordpress' : 'hosting-fix-netlify');

export function formatFixQueueNote(input: {
  account: Pick<HostingAccount, 'name'>;
  clientName: string | null;
  run: HostingRun;
  siteUrl: string;
  target: FixTarget;
  agentItems: FixItem[];
  personItems: FixItem[];
  template?: string;
}): string {
  const { account, clientName, run, siteUrl, target, agentItems, personItems } = input;
  const vars = {
    client: clientName || account.name,
    account: account.name,
    site_url: siteUrl,
    run_when: new Date(run.finished_at || run.created_at).toLocaleString(),
    run_id: run.id,
    fixes: agentItems.map((i, n) => `${n + 1}. [${i.urgency}] ${i.title}\n   ${i.action}`).join('\n'),
    not_yours: personItems.length
      ? ['Not yours (a person handles these; mention them in the report only if your change affects them):', ...personItems.map((i) => `- ${i.title}`)].join('\n')
      : '',
  };
  const template = input.template ?? PROMPT_DEFAULTS[fixPromptId(target)];
  if (target.platform === 'cloudways') return renderPrompt(template, { ...vars, server_id: target.server_id, app_id: target.app_id });
  return renderPrompt(template, { ...vars, repo: target.repo, branch: target.branch, netlify_site: target.netlify ? ` · Netlify site: ${target.netlify}` : '' });
}

/** Task + spec-queue job for one site's agent-fixable findings. */
export async function enqueueHostingFix(input: {
  account: HostingAccount;
  clientName: string | null;
  run: HostingRun;
  site: HostedSite;
  steps: HostingRunStep[];
  items: FixItem[];
  createdBy: { id: string; name: string };
}): Promise<AiJob> {
  const { account, run, site, steps, createdBy } = input;
  const target = fixTargetOf(steps, site);
  if (!target) throw new Error('This site has no repo or Cloudways app on record, so an agent has no way in.');
  const mine = input.items.filter((i) => i.site_id === site.id || i.site_id === null);
  const agentItems = mine.filter((i) => i.owner === 'agent');
  const personItems = mine.filter((i) => i.owner === 'person');
  if (!agentItems.length) throw new Error('Nothing in this run is for an agent to fix.');
  const title = `Hosting fixes — ${account.name} (${host(site.url)})`;
  const template = await loadPromptTemplate(fixPromptId(target));
  const queueNote = formatFixQueueNote({ account, clientName: input.clientName, run, siteUrl: site.url, target, agentItems, personItems, template });

  const now = new Date();
  const { data: task, error: taskError } = await supabase
    .from('client_tasks')
    .insert({
      client_id: account.client_id,
      title,
      description: queueNote,
      status: 'In Progress',
      due_date: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12).toISOString(),
      assigned_by: createdBy.id,
      assigned_to: createdBy.id,
      assigned_to_name: createdBy.name,
    })
    .select('id')
    .single();
  if (taskError) throw taskError;

  const brief: HostingFixBrief = {
    hosting_fix: {
      run_id: run.id,
      account_id: account.id,
      site_id: site.id,
      site_url: site.url,
      platform: target.platform,
      ...(target.platform === 'cloudways'
        ? { cloudways_server_id: target.server_id, cloudways_app_id: target.app_id }
        : { repo: target.repo, branch: target.branch }),
      items: agentItems.map(({ urgency, title: t, action, step }) => ({ urgency, title: t, action, step })),
    },
  };
  return enqueueAiJob({ task_id: task.id, title, created_by: createdBy.id, queue_note: queueNote, kind: 'fulfillment', brief, model: HOSTING_FIX_MODEL });
}

/** A run's site job, if one was sent. A cancelled job doesn't count: the site can be sent again. */
export const fixJobFor = (jobs: AiJob[], siteId: string) =>
  jobs.find((j) => j.status !== 'cancelled' && (j.brief as HostingFixBrief | null)?.hosting_fix?.site_id === siteId) || null;

/** Sites whose agent work could go to the queue: has agent items and a repo or Cloudways app on record. */
export const sendableFixSites = (steps: HostingRunStep[], sites: HostedSite[], items: FixItem[]) =>
  sites.filter((s) => items.some((i) => i.site_id === s.id && i.owner === 'agent') && fixTargetOf(steps, s));

/** One account's latest finished run, and the sites in it that have a fix to send and no job yet. */
export type PendingFix = { run: HostingRun; steps: HostingRunStep[]; items: FixItem[]; sites: HostedSite[] };

/**
 * For the Hosting list: which accounts' latest run still has a hosting fix to
 * send, the same rule the run's Fix plan uses. Two queries for every account
 * (the runs' steps, the jobs those runs spawned), reduced here.
 */
export async function fetchPendingFixes(latestRuns: Record<string, HostingRun>, sites: HostedSite[]): Promise<Record<string, PendingFix>> {
  const runs = Object.values(latestRuns).filter((r) => r.status === 'attention' || r.status === 'failed');
  if (!runs.length) return {};
  const ids = runs.map((r) => r.id);
  const [allSteps, jobsRes] = await Promise.all([
    fetchRunStepsFor(ids),
    supabase.from('ai_jobs').select('*').in('brief->hosting_fix->>run_id', ids),
  ]);
  if (jobsRes.error) throw jobsRes.error;
  const allJobs = (jobsRes.data || []) as AiJob[];
  const out: Record<string, PendingFix> = {};
  for (const run of runs) {
    const steps = allSteps.filter((s) => s.run_id === run.id);
    const mine = sites.filter((s) => s.hosting_account_id === run.hosting_account_id);
    const items = buildFixPlan(steps, mine);
    const jobs = allJobs.filter((j) => (j.brief as HostingFixBrief | null)?.hosting_fix?.run_id === run.id);
    const pending = sendableFixSites(steps, mine, items).filter((s) => !fixJobFor(jobs, s.id));
    if (pending.length) out[run.hosting_account_id] = { run, steps, items, sites: pending };
  }
  return out;
}

/** Jobs this run already spawned (newest first). */
export async function fetchFixJobs(runId: string): Promise<AiJob[]> {
  const { data, error } = await supabase.from('ai_jobs').select('*').eq('brief->hosting_fix->>run_id', runId).order('created_at', { ascending: false });
  if (error) throw error;
  return (data || []) as AiJob[];
}

/** Hosting fix jobs still being worked (anything short of complete), by site id. */
export async function fetchOpenFixJobsBySite(): Promise<Record<string, AiJob>> {
  const { data, error } = await supabase
    .from('ai_jobs')
    .select('*')
    .not('brief->hosting_fix', 'is', null)
    .not('status', 'in', '(complete,submitted,cancelled)')
    .order('created_at', { ascending: false });
  if (error) throw error;
  const bySite: Record<string, AiJob> = {};
  for (const job of (data || []) as AiJob[]) {
    const siteId = (job.brief as HostingFixBrief | null)?.hosting_fix?.site_id;
    if (siteId && !bySite[siteId]) bySite[siteId] = job;
  }
  return bySite;
}
